import { Module } from 'vuex';
import {
    RESET_STATE,
    SET_USER,
    SET_USER_STATE_ITEM,
    SET_CONTACT,
    UserMutations,
} from '@/modules/users/store/user.mutations';
import {
    FETCH_CONTACT,
    FETCH_USER,
    LOG_OUT,
    LOGIN_USER,
    SET_USER_PROFILE,
    UPDATE_USER,
    UserActions,
} from '@/modules/users/store/user.actions';
import APIService from '@/modules/shared/shared/domain/api';
import { Resources, Actions } from '@/modules/shared/shared/domain/constants/constants';
import {
    hasPrivilegesOf,
    hasPrivilegesOfAction,
} from '@/modules/users/domain/functions/user.functions';
import { UserInterface } from '@/modules/users/domain/user.interface';
import {
    GET_CUSTOMER_ID,
    GET_CUSTOMER_NAME,
    GET_CUSTOMER_TYPE_ID,
    GET_ROLE_ID,
    GET_USER_CONTACT,
    GET_USER_ID,
    HAS_PRIVILEGES_OF_READ_TICKETS,
    HAS_PRIVILEGES_OVER_CONTACTS,
    HAS_PRIVILEGES_OVER_CUSTOMERS,
    HAS_PRIVILEGES_OVER_DIRECTORY_GROUPS,
    HAS_PRIVILEGES_OVER_EQUIPMENTS,
    HAS_PRIVILEGES_OVER_FEEDBACKS,
    HAS_PRIVILEGES_OVER_FINANCES,
    HAS_PRIVILEGES_OVER_HEALTH_REPORTS,
    HAS_PRIVILEGES_OVER_PDT,
    HAS_PRIVILEGES_OVER_REPORTS,
    HAS_PRIVILEGES_OVER_TICKETS,
    IS_EMPLOYEE,
    IS_CONTACT_ADMINISTRATOR,
    UserGetters,
} from '@/modules/users/store/user.getters';
const apiService = new APIService();
import { usersRepository } from '@/components/users/repositories/users.repository';
import { tokensRepository } from '@/components/users/repositories/tokens.repository';
import { userRepository } from '@/modules/users/repositories/users.repository';
import { Exception } from '@/modules/shared/shared/domain/exceptions/exception';
import { RecoveryPasswordRequest } from '@/modules/users/domain/recovery-password-request';
import { User } from '@/modules/users/domain/user';
import { Customer } from '@/modules/customers/domain/customer';
import { LookupResponse } from '@/modules/users/domain/lookup-response';
import { LoginRequest } from '@/modules/users/domain/login-request';
import { USER_EMPTY_STATE } from '@/modules/users/store/user.states';
import { defineAbilitiesFor } from '@/plugins/acl/acl-functions';
import ability from '@/plugins/acl/ability';

const user: Module<User, any> = {
    namespaced: true,
    state: {
        ...USER_EMPTY_STATE,
    },
    getters: {
        [GET_USER_ID](state): number {
            return state.user.id;
        },
        [GET_CUSTOMER_ID](state): number {
            return state.contact.customer.id;
        },
        [GET_CUSTOMER_NAME](state): string {
            return state.contact.customer.name;
        },
        [GET_USER_CONTACT](state): object {
            return state.contact;
        },
        [GET_CUSTOMER_TYPE_ID](): number {
            return 3; // FIX: Por defecto está como cliente pero no está asociado a base de datos
        },
        [GET_ROLE_ID](state): number {
            return state.role.id;
        },
        [HAS_PRIVILEGES_OVER_TICKETS](state): boolean {
            return hasPrivilegesOf(state.role.rolePrivileges, Resources.TICKETS.id) || true;
        },
        [HAS_PRIVILEGES_OVER_PDT](state): boolean {
            return hasPrivilegesOf(state.role.rolePrivileges, Resources.PDT.id);
        },
        [HAS_PRIVILEGES_OVER_FEEDBACKS](state, getters): boolean {
            return (
                (hasPrivilegesOf(state.role.rolePrivileges, Resources.FEEDBACKS.id) || true) &&
                getters[IS_EMPLOYEE]
            );
        },
        [HAS_PRIVILEGES_OVER_REPORTS](state): boolean {
            return hasPrivilegesOf(state.role.rolePrivileges, Resources.REPORTS.id) || true;
        },
        [HAS_PRIVILEGES_OVER_HEALTH_REPORTS](state): boolean {
            return hasPrivilegesOf(state.role.rolePrivileges, Resources.HEALTH_REPORTS.id) || true;
        },
        [HAS_PRIVILEGES_OVER_FINANCES](state): boolean {
            return hasPrivilegesOf(state.role.rolePrivileges, Resources.FINANCES.id) || true;
        },
        [HAS_PRIVILEGES_OVER_DIRECTORY_GROUPS](state): boolean {
            return (
                hasPrivilegesOf(state.role.rolePrivileges, Resources.ACTIVE_DIRECTORY_GROUPS.id) ||
                true
            );
        },
        [HAS_PRIVILEGES_OVER_CUSTOMERS](state): boolean {
            return hasPrivilegesOf(state.role.rolePrivileges, Resources.CUSTOMERS.id) || true;
        },
        [HAS_PRIVILEGES_OVER_EQUIPMENTS](state): boolean {
            return hasPrivilegesOf(state.role.rolePrivileges, Resources.EQUIPMENTS.id) || true;
        },
        [HAS_PRIVILEGES_OVER_CONTACTS](state): boolean {
            return hasPrivilegesOf(state.role.rolePrivileges, Resources.CONTACTS.id) || true;
        },
        [IS_EMPLOYEE](state): boolean {
            return state.user.origin === process.env.VUE_APP_USER_ORIGIN;
        },
        [HAS_PRIVILEGES_OF_READ_TICKETS](state): boolean {
            return (
                hasPrivilegesOfAction(
                    state.role.rolePrivileges,
                    Resources.TICKETS.id,
                    Actions.READ.id,
                ) || true
            );
        },
        [UserGetters.IS_CONTACT_ADMINISTRATOR](state): boolean {
            return hasPrivilegesOfAction(
                state.role.rolePrivileges,
                Resources.PRIVILEGE_ADMINISTRATION.id,
                Actions.CREATE.id,
            );
        },
        [UserGetters.GET_USER_ROLE](state) {
            return state.role;
        },
        [UserGetters.GET_CURRENT_USER](state): User {
            return state;
        },
        [UserGetters.GET_CUSTOMER_OPTIONS](state): Customer[] | { id: number } {
            return state.customerOptions;
        },
    },
    mutations: {
        [SET_USER](state, user: User): void {
            Object.assign(state, user);
        },
        [SET_CONTACT](state, contact: any): void {
            Object.assign(state.contact, contact);
        },
        [SET_USER_STATE_ITEM](state, { item, value }): void {
            state[item] = value;
        },
        [RESET_STATE](state): void {
            Object.assign(state, USER_EMPTY_STATE);
        },
        [UserMutations.SET_CUSTOMER_OPTIONS](state, options: LookupResponse): void {
            if (options instanceof Array) {
                state.customerOptions = options;
            } else {
                state.customerOptions = { id: options.customerId };
            }
        },
    },
    actions: {
        async [UserActions.LOOK_UP]({ commit }, username: string): Promise<RequestResponse> {
            try {
                const response = await userRepository.lookUp(username);
                commit(UserMutations.SET_CUSTOMER_OPTIONS, response);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },
        async [UserActions.RECOVERY_PASSWORD](
            { commit },
            recoveryPasswordRequest: RecoveryPasswordRequest,
        ): Promise<RequestResponse> {
            try {
                await userRepository.recoveryPassword(recoveryPasswordRequest);
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },
        async [UserActions.LOGIN](
            { commit, state, dispatch },
            userLogin: LoginRequest,
        ): Promise<RequestResponse> {
            try {
                const user = await userRepository.login(userLogin);
                commit(SET_USER, user);
                ability.update(defineAbilitiesFor(user.role) as any);
                localStorage.setItem('token', state.access_token);
                localStorage.setItem('company', JSON.stringify(user.contact.customer));
                return false;
            } catch (e) {
                return new Exception(e.response);
            }
        },
        async [LOG_OUT]({ commit, state }): Promise<boolean> {
            let result: boolean = false;
            try {
                await tokensRepository.logout();
                commit(RESET_STATE);
                localStorage.clear();
                sessionStorage.clear();
                result = true;
            } catch (e) {
                localStorage.clear();
                sessionStorage.clear();
            }
            return result;
        },
        async [FETCH_USER]({ commit }): Promise<boolean> {
            try {
                const token = '' + localStorage.getItem('token');
                const user: UserInterface = <UserInterface>(await apiService.getUser(token)).data;
                commit(SET_USER, user);
                return true;
            } catch (e) {
                return false;
            }
        },
        async [FETCH_CONTACT]({ commit, getters, state }, userId: number): Promise<boolean> {
            try {
                const contact = (await apiService.getContact(userId || state.user.id)).data.contact;
                commit(SET_CONTACT, contact);
                return true;
            } catch (e) {
                return false;
            }
        },
        async [UPDATE_USER]({ commit }, user): Promise<boolean> {
            try {
                await usersRepository.updateUser(user);
                return true;
            } catch (e) {
                return false;
            }
        },
    },
};

export default user;
